import { Link, useSearchParams } from 'react-router-dom';

export interface PageTab {
  /** `?tab=` value. The first tab in a group is the default and omits the param. */
  key: string;
  label: string;
}

/**
 * Sub-views of one page, selected by `?tab=`. Pages that used to be separate
 * sidebar entries live here instead — the tab is a real link, so a view can be
 * bookmarked, opened in a new tab, and redirected to from its retired route.
 */
export const PageTabs = ({ tabs, current }: { tabs: readonly PageTab[]; current: string }) => {
  const [sp] = useSearchParams();

  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
      {tabs.map((t, i) => {
        const active = t.key === current;
        // Preserve any other query state (filters, selected client) across tabs.
        const next = new URLSearchParams(sp);
        if (i === 0) next.delete('tab');
        else next.set('tab', t.key);
        const qs = next.toString();
        return (
          <Link
            key={t.key}
            // Search-only target: keeps the current path without depending on
            // how this page happens to be nested in the route tree.
            to={{ search: qs ? `?${qs}` : '' }}
            replace
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: '1px solid var(--p-border)',
              background: active ? 'var(--p-primary)' : '#fff',
              color: active ? '#fff' : 'var(--p-muted-fg)',
              fontSize: 12,
              fontWeight: active ? 600 : 400,
              textDecoration: 'none',
            }}
            aria-current={active ? 'page' : undefined}
          >
            {t.label}
          </Link>
        );
      })}
    </div>
  );
};

/**
 * Resolve `?tab=` against a group's tabs, falling back to the first. Returns the
 * key rather than the tab so callers can switch on it exhaustively.
 */
export const useTab = (tabs: readonly PageTab[]): string => {
  const [sp] = useSearchParams();
  const requested = sp.get('tab');
  return tabs.some((t) => t.key === requested) ? requested! : tabs[0]!.key;
};
